import { HttpClient, HttpParams } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { Observable, catchError, map, of, throwError } from "rxjs";
import { environment } from "src/environments/environment";
import { NavigationMenuSelection } from "../interfaces/navigation_menu.interface";

import { AssessmentFooterPage, AssessmentFooterPageQuery, readAssessmentFooterPage } from "./assessment_footer_page";

interface ApiResponse<T> {
    success: boolean;
    data?: T;
    message?: string;
}

export interface QuestionResponseLinkDto {
    id: number;
    displayName?: string | null;
    display_name?: string | null;
    url: string;
    type?: string | null;
    linkType?: string | null;
    link_type?: string | null;
    createdAt?: string | null;
    created_at?: string | null;
    updatedAt?: string | null;
    updated_at?: string | null;
    questionReference?: string | null;
    question_reference?: string | null;
    createdByDisplayName?: string | null;
    created_by_display_name?: string | null;
    userId?: number | null;
    user_id?: number | null;
}

export interface CreateQuestionResponseLinkPayload {
    displayName: string;
    url: string;
    type: string;
}

@Injectable({ providedIn: "root" })
export class QuestionResponseLinksService {
    private static readonly SECTION_PATTERN = /^[A-Za-z0-9_]+$/;
    private static readonly TARGET_PATTERN = /^[A-Za-z0-9 _-]+$/;

    private readonly apiBaseUrl = environment.baseurl.replace(/\/$/, "");
    private activeProductLicenseId: number | null = null;

    constructor(private readonly http: HttpClient) {}

    setActiveProductLicenseId(productLicenseId: number | null): void {
        if (productLicenseId === null || productLicenseId === undefined) {
            this.activeProductLicenseId = null;
            return;
        }

        const normalized = Number(productLicenseId);
        this.activeProductLicenseId = Number.isInteger(normalized) && normalized > 0 ? normalized : null;
    }

    listLinks(
        selection?: NavigationMenuSelection | null,
        productLicenseId?: number
    ): Observable<QuestionResponseLinkDto[]> {
        const licenseId = this.resolveProductLicenseId(productLicenseId);
        if (licenseId === null) {
            return throwError(() => new Error("A product license must be selected before loading supporting documentation."));
        }

        const normalized = this.normalizeSelection(selection ?? null);
        const query: Record<string, string> = { productLicenseId: String(licenseId) };
        if (normalized) {
            query["section"] = normalized.section;
            query["target"] = normalized.target;
        }

        const params = new HttpParams({ fromObject: query });

        return this.http
            .get<ApiResponse<QuestionResponseLinkDto[]>>(`${this.apiBaseUrl}/question-response-links`, { params })
            .pipe(
                map((response) => (response?.success && Array.isArray(response.data) ? response.data : [])),
                catchError((error) => {
                    if (error?.status === 404) {
                        return of([]);
                    }
                    return throwError(() => error);
                })
            );
    }

    listPage(selection: NavigationMenuSelection | null, productLicenseId: number,
        query: AssessmentFooterPageQuery): Observable<AssessmentFooterPage<QuestionResponseLinkDto>> {
        const licenseId = this.resolveProductLicenseId(productLicenseId);
        if (licenseId === null) return throwError(() => new Error("An assessment must be selected."));
        const normalized = this.normalizeSelection(selection);
        const params = new HttpParams({ fromObject: {
            productLicenseId: String(licenseId), page: String(query.page), pageSize: String(query.pageSize),
            search: query.search, authors: JSON.stringify(query.authors),
            ...(normalized ? { section: normalized.section, target: normalized.target } : {})
        } });
        return this.http.get<ApiResponse<AssessmentFooterPage<QuestionResponseLinkDto>>>(
            `${this.apiBaseUrl}/question-response-links`, { params }).pipe(map(response => {
                if (!response?.success) throw new Error(response?.message || "Unable to load assessment footer.");
                return readAssessmentFooterPage(response.data, query);
            }));
    }

    createLink(
        selection: NavigationMenuSelection | null | undefined,
        payload: CreateQuestionResponseLinkPayload,
        productLicenseId?: number
    ): Observable<QuestionResponseLinkDto> {
        const licenseId = this.resolveProductLicenseId(productLicenseId);
        if (licenseId === null) {
            return throwError(() => new Error("A product license must be selected before adding supporting documentation."));
        }

        const normalized = this.normalizeSelection(selection ?? null);

        const body: Record<string, unknown> = {
            productLicenseId: licenseId,
            displayName: payload.displayName,
            url: payload.url,
            type: payload.type
        };

        if (normalized) {
            body["section"] = normalized.section;
            body["target"] = normalized.target;
        }

        return this.http
            .post<ApiResponse<QuestionResponseLinkDto>>(`${this.apiBaseUrl}/question-response-links`, body)
            .pipe(
                map((response) => {
                    if (!response?.success || !response.data) {
                        throw new Error(response?.message || "Failed to add supporting documentation link.");
                    }
                    return response.data;
                })
            );
    }

    updateLink(
        linkId: number,
        selection: NavigationMenuSelection | null,
        payload: CreateQuestionResponseLinkPayload,
        productLicenseId?: number
    ): Observable<QuestionResponseLinkDto> {
        const licenseId = this.resolveProductLicenseId(productLicenseId);
        if (licenseId === null || !Number.isInteger(linkId) || linkId <= 0) {
            return throwError(() => new Error("A valid assessment and supporting documentation entry are required."));
        }
        const normalized = this.normalizeSelection(selection);
        const body: Record<string, unknown> = { productLicenseId: licenseId, ...payload };
        if (normalized) {
            body["section"] = normalized.section;
            body["target"] = normalized.target;
        }
        return this.http.put<ApiResponse<QuestionResponseLinkDto>>(`${this.apiBaseUrl}/question-response-links/${linkId}`, body)
            .pipe(map((response) => {
                if (!response?.success || !response.data) {
                    throw new Error(response?.message || "Failed to update supporting documentation link.");
                }
                return response.data;
            }));
    }

    deleteLink(
        linkId: number,
        selection?: NavigationMenuSelection | null,
        productLicenseId?: number
    ): Observable<void> {
        const licenseId = this.resolveProductLicenseId(productLicenseId);
        if (licenseId === null) {
            return throwError(() => new Error("A product license must be selected before removing supporting documentation."));
        }

        const normalized = this.normalizeSelection(selection ?? null);

        if (!Number.isInteger(linkId) || linkId <= 0) {
            return throwError(() => new Error("A valid supporting documentation identifier is required."));
        }

        const query: Record<string, string> = { productLicenseId: String(licenseId) };
        if (normalized) {
            query["section"] = normalized.section;
            query["target"] = normalized.target;
        }

        const params = new HttpParams({ fromObject: query });

        return this.http
            .delete<ApiResponse<{ id: number }>>(`${this.apiBaseUrl}/question-response-links/${linkId}`, { params })
            .pipe(
                map((response) => {
                    if (!response?.success) {
                        throw new Error(response?.message || "Failed to delete supporting documentation link.");
                    }
                })
            );
    }

    private resolveProductLicenseId(productLicenseId?: number): number | null {
        const candidate = productLicenseId ?? this.activeProductLicenseId;
        const normalized = Number(candidate);

        if (!Number.isInteger(normalized) || normalized <= 0) {
            return null;
        }

        return normalized;
    }

    private normalizeSelection(selection: NavigationMenuSelection | null | undefined): NavigationMenuSelection | null {
        if (!selection) {
            return null;
        }

        const section = typeof selection.section === "string" ? selection.section.trim() : "";
        const target = typeof selection.target === "string" ? selection.target.trim() : "";

        if (!section || !target) {
            return null;
        }

        if (!QuestionResponseLinksService.SECTION_PATTERN.test(section)) {
            return null;
        }

        if (!QuestionResponseLinksService.TARGET_PATTERN.test(target)) {
            return null;
        }

        return { section, target };
    }
}
